import { Fragment, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TbFileText, TbLoader2, TbX, TbSend, TbMailOff, TbEye } from "react-icons/tb";
import type { sendPlan } from "./sendPlan";
import { PreviewSchedule } from "./PreviewSchedule";
import type { Patient, Recipient, Doctor } from "src/types";
import { activeRows, draftLineAmount, type GoaLineDraft } from "src/components/GoaLineEditor/goaLineDraft";
import { displayCode } from "src/helpers/goaRules";
import { formatEuro } from "./StepServices";
import { doctorName } from "./StepTreatment";
import { formatDay } from "./treatmentRules";
import { formatBirthDate, patientName } from "./patientSearch";
import { postForBlob } from "./postForBlob";

const fetchPreviewPdf = (body: unknown) => postForBlob("/invoices/manual/preview-pdf", body);

/** Step 5: the invoice as it will be created — who it goes to, what's billed, the totals, what
 *  happens if it's sent today, and the PDF exactly as it will look (number "VORSCHAU"). */
export function StepPreview({
    patient,
    recipient,
    owner,
    days,
    services,
    diagnoses,
    totals,
    pdfBody,
    send,
}: {
    patient: Patient | null;
    recipient: Recipient | null;
    owner: Doctor | null;
    days: string[];
    services: Record<string, GoaLineDraft[]>;
    diagnoses: string[];
    totals: { fees: number; material: number; total: number };
    /** Same body as POST /invoices/manual — null while the invoice isn't complete. */
    pdfBody: unknown | null;
    /** Story 12: what *Erstellen & senden* would do — said here, next to the buttons that do it. */
    send: ReturnType<typeof sendPlan>;
}) {
    const { t } = useTranslation();
    const p = (key: string, opts?: Record<string, unknown>) => t(`invoiceCreate.preview.${key}`, opts);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState(false);

    const closePdf = () => {
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
    };
    useEffect(() => () => {
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    }, [pdfUrl]);

    const openPdf = async () => {
        if (!pdfBody || pdfLoading) return;
        setPdfLoading(true);
        setPdfError(false);
        try {
            setPdfUrl(await fetchPreviewPdf(pdfBody));
        } catch {
            setPdfError(true);
        } finally {
            setPdfLoading(false);
        }
    };

    return (
        <div className="ic-preview">
            {/* Its own card at the top — testers overlooked the PDF as a plain secondary button. */}
            <div className="ic-pdf-card">
                <span className="ic-pdf-card__icon" aria-hidden><TbFileText /></span>
                <div className="ic-pdf-card__text">
                    <span className="ic-pdf-card__title">{p("pdfCardTitle")}</span>
                    <span className="ic-pdf-card__lead">{p("pdfCardLead")}</span>
                    {!pdfBody && <span className="ic-field__hint">{p("pdfIncomplete")}</span>}
                    {pdfError && <span className="ic-field__hint ic-field__hint--error" role="alert">{p("pdfFailed")}</span>}
                </div>
                <button type="button" className="ic-pdf-card__btn" onClick={() => void openPdf()} disabled={!pdfBody || pdfLoading} aria-busy={pdfLoading}>
                    {pdfLoading ? <TbLoader2 className="ic-spin" aria-hidden /> : <TbEye aria-hidden />}
                    {pdfLoading ? p("pdfLoading") : p("viewPdf")}
                </button>
            </div>

            {send && (
                <div className={`ic-note ${send.kind === "none" ? "ic-note--neutral" : "ic-note--info"}`} data-testid="ic-send-plan">
                    {send.kind === "none" ? <TbMailOff aria-hidden /> : <TbSend aria-hidden />}
                    <span>
                        {send.kind === "send"
                            ? p("sendNow", { email: send.email })
                            : send.kind === "afterConsent"
                              ? p("sendAfterConsent", { email: send.email })
                              : `${p("sendNone")} ${p(`sendNoneReasons.${send.reason}`)}`}
                    </span>
                </div>
            )}

            {pdfUrl && createPortal(
                <div className="ic-pdf-overlay" onClick={closePdf}>
                    <div className="ic-pdf-modal" role="dialog" aria-modal="true" aria-label={p("pdfTitle")} onClick={(e) => e.stopPropagation()}>
                        <div className="ic-pdf-modal__header">
                            <span>{p("pdfTitle")}</span>
                            <button type="button" className="ic-pdf-modal__close" onClick={closePdf} aria-label={t("popup.close", { defaultValue: "Schließen" })}>
                                <TbX aria-hidden />
                            </button>
                        </div>
                        <iframe className="ic-pdf-modal__frame" src={pdfUrl} title={p("pdfTitle")} />
                    </div>
                </div>,
                document.body,
            )}

            <div className="ic-preview__parties">
                <div>
                    <span className="ic-preview__label">{p("to")}</span>
                    {recipient && (
                        <address className="ic-preview__address">
                            <strong>{patientName(recipient)}</strong>
                            {recipient.street && <span>{recipient.street}</span>}
                            {(recipient.plz || recipient.city) && <span>{[recipient.plz, recipient.city].filter(Boolean).join(" ")}</span>}
                        </address>
                    )}
                </div>
                <div>
                    <span className="ic-preview__label">{p("patient")}</span>
                    {patient && (
                        <span className="ic-preview__value">
                            {patientName(patient)} · {t("invoiceCreate.patient.born", { date: formatBirthDate(patient.birthDate) })}
                        </span>
                    )}
                    <span className="ic-preview__label">{p("doctor")}</span>
                    {owner && <span className="ic-preview__value">{doctorName(owner)}</span>}
                </div>
            </div>

            {diagnoses.length > 0 && (
                <div className="ic-preview__block">
                    <span className="ic-preview__label">{t("invoiceCreate.treatment.diagnoses")}</span>
                    <ol className="ic-preview__diagnoses">
                        {diagnoses.map((d) => <li key={d}>{d}</li>)}
                    </ol>
                </div>
            )}

            <div className="ic-preview__table-wrap">
                <table className="ic-preview__table">
                    <thead>
                        <tr>
                            <th scope="col">{p("colCode")}</th>
                            <th scope="col">{p("colDescription")}</th>
                            <th scope="col" className="ic-num">{p("colQty")}</th>
                            <th scope="col" className="ic-num">{p("colFactor")}</th>
                            <th scope="col" className="ic-num">{p("colAmount")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {days.map((day) => (
                            <Fragment key={day}>
                                <tr className="ic-preview__day">
                                    <th scope="rowgroup" colSpan={5}>{formatDay(day)}</th>
                                </tr>
                                {activeRows(services[day] ?? []).map((row) => (
                                    <Fragment key={row._localId}>
                                        <tr>
                                            <td>{displayCode(row.code)}</td>
                                            <td>
                                                {row.description}
                                                {row.justification.trim() && <span className="ic-preview__justification">{p("justification", { text: row.justification.trim() })}</span>}
                                            </td>
                                            <td className="ic-num">{row.quantity}</td>
                                            <td className="ic-num">{row.factor.toLocaleString("de-DE", { minimumFractionDigits: 1 })}</td>
                                            <td className="ic-num">{formatEuro(draftLineAmount(row))}</td>
                                        </tr>
                                        {row.materialLines.map((ml) => (
                                            <tr key={ml._localId} className="ic-preview__material">
                                                <td />
                                                <td>{ml.label}</td>
                                                <td />
                                                <td />
                                                <td className="ic-num">{formatEuro(ml.cost)}</td>
                                            </tr>
                                        ))}
                                    </Fragment>
                                ))}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <dl className="ic-preview__totals">
                <div><dt>{p("fees")}</dt><dd>{formatEuro(totals.fees)}</dd></div>
                {totals.material > 0 && <div><dt>{p("material")}</dt><dd>{formatEuro(totals.material)}</dd></div>}
                <div className="ic-preview__grand"><dt>{p("total")}</dt><dd>{formatEuro(totals.total)}</dd></div>
            </dl>

            <PreviewSchedule />

            <p className="ic-preview__note">{p("afterCreate")}</p>
        </div>
    );
}
